'use client';

import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/routing';
import { Shield, Clock, Video, CheckCircle, ChevronDown, ArrowRight } from 'lucide-react';
import Logo from '@/components/layout/Logo';
import styles from './page.module.css';

export default function DiscoveryCallPage() {
  const t = useTranslations('discoveryCall');

  return (
    <div className={styles.page}>
      {/* Minimal Header */}
      <header className={styles.header}>
        <Link href="/" className={styles.logoLink}>
          <Logo />
        </Link>
      </header>

      <main className={styles.main}>
        {/* Hero Section */}
        <section className={styles.hero}>
          <span className={styles.label}>{t('badge')}</span>
          <h1 className={styles.title}>{t('title')}</h1>
          <p className={styles.subtitle}>{t('subtitle')}</p>

          <div className={styles.meta}>
            <div className={styles.metaItem}>
              <Clock size={16} />
              <span>{t('duration')}</span>
            </div>
            <span className={styles.metaDivider} />
            <div className={styles.metaItem}>
              <Video size={16} />
              <span>{t('format')}</span>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className={styles.pricingSection}>
          <div className={styles.pricingContent}>
            <div className={styles.priceDisplay}>
              <span className={styles.currentPrice}>$399</span>
            </div>
            <p className={styles.priceNote}>{t('pricing.note')}</p>
            <p className={styles.priceNote} style={{ color: 'var(--accent)', fontWeight: 600 }}>{t('pricing.feeCredit')}</p>

            <Link href="/discovery-call/intake" className={styles.ctaButton}>
              <span>{t('bottomCta.button')}</span>
              <ArrowRight size={18} />
            </Link>

            <div className={styles.guarantee}>
              <Shield size={14} />
              <span>{t('guarantee')}</span>
            </div>

            <p className={styles.disclaimer}>{t('disclaimers.refund')}</p>
            <p className={styles.disclaimer}>{t('disclaimers.noShow')}</p>
          </div>
        </section>

        {/* What You Get - Timeline Style */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.label}>INCLUDED</span>
            <h2 className={styles.sectionTitle}>{t('whatYouGet.title')}</h2>
          </div>

          <div className={styles.benefits}>
            <div className={styles.benefitItem}>
              <div className={styles.benefitNumber}>01</div>
              <div className={styles.benefitContent}>
                <h3 className={styles.benefitTitle}>{t('whatYouGet.benefit1.title')}</h3>
                <p className={styles.benefitDesc}>{t('whatYouGet.benefit1.description')}</p>
              </div>
            </div>
            <div className={styles.benefitItem}>
              <div className={styles.benefitNumber}>02</div>
              <div className={styles.benefitContent}>
                <h3 className={styles.benefitTitle}>{t('whatYouGet.benefit2.title')}</h3>
                <p className={styles.benefitDesc}>{t('whatYouGet.benefit2.description')}</p>
              </div>
            </div>
            <div className={styles.benefitItem}>
              <div className={styles.benefitNumber}>03</div>
              <div className={styles.benefitContent}>
                <h3 className={styles.benefitTitle}>{t('whatYouGet.benefit3.title')}</h3>
                <p className={styles.benefitDesc}>{t('whatYouGet.benefit3.description')}</p>
              </div>
            </div>
            <div className={styles.benefitItem}>
              <div className={styles.benefitNumber}>04</div>
              <div className={styles.benefitContent}>
                <h3 className={styles.benefitTitle}>{t('whatYouGet.benefit4.title')}</h3>
                <p className={styles.benefitDesc}>{t('whatYouGet.benefit4.description')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* What's next: bridge to the retainer (hire-us-or-not) */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.label}>{t('whatsNext.label')}</span>
            <h2 className={styles.sectionTitle}>{t('whatsNext.title')}</h2>
          </div>
          <p className={styles.subtitle} style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto' }}>
            {t('whatsNext.body')}
          </p>
        </section>

        {/* Who Is This For */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.label}>IDEAL FOR</span>
            <h2 className={styles.sectionTitle}>{t('whoIsThisFor.title')}</h2>
          </div>

          <ul className={styles.audienceList}>
            <li>
              <CheckCircle size={16} />
              <span>{t('whoIsThisFor.audience1')}</span>
            </li>
            <li>
              <CheckCircle size={16} />
              <span>{t('whoIsThisFor.audience2')}</span>
            </li>
            <li>
              <CheckCircle size={16} />
              <span>{t('whoIsThisFor.audience3')}</span>
            </li>
            <li>
              <CheckCircle size={16} />
              <span>{t('whoIsThisFor.audience4')}</span>
            </li>
          </ul>
        </section>

        {/* Agenda */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.label}>AGENDA</span>
            <h2 className={styles.sectionTitle}>{t('agenda.title')}</h2>
          </div>

          <div className={styles.agenda}>
            <div className={styles.agendaItem}>
              <span className={styles.agendaTime}>{t('agenda.item1.title')}</span>
              <span className={styles.agendaDesc}>{t('agenda.item1.description')}</span>
            </div>
            <div className={styles.agendaItem}>
              <span className={styles.agendaTime}>{t('agenda.item2.title')}</span>
              <span className={styles.agendaDesc}>{t('agenda.item2.description')}</span>
            </div>
            <div className={styles.agendaItem}>
              <span className={styles.agendaTime}>{t('agenda.item3.title')}</span>
              <span className={styles.agendaDesc}>{t('agenda.item3.description')}</span>
            </div>
            <div className={styles.agendaItem}>
              <span className={styles.agendaTime}>{t('agenda.item4.title')}</span>
              <span className={styles.agendaDesc}>{t('agenda.item4.description')}</span>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.label}>FAQ</span>
            <h2 className={styles.sectionTitle}>{t('faq.title')}</h2>
          </div>

          <div className={styles.faqList}>
            <details className={styles.faqItem}>
              <summary>
                <span>{t('faq.q1')}</span>
                <ChevronDown size={18} />
              </summary>
              <p>{t('faq.a1')}</p>
            </details>
            <details className={styles.faqItem}>
              <summary>
                <span>{t('faq.q2')}</span>
                <ChevronDown size={18} />
              </summary>
              <p>{t('faq.a2')}</p>
            </details>
            <details className={styles.faqItem}>
              <summary>
                <span>{t('faq.q3')}</span>
                <ChevronDown size={18} />
              </summary>
              <p>{t('faq.a3')}</p>
            </details>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className={styles.finalCta}>
          <h2 className={styles.finalCtaTitle}>{t('bottomCta.title')}</h2>
          <p className={styles.finalCtaSubtitle}>{t('bottomCta.subtitle')}</p>
          <Link href="/discovery-call/intake" className={styles.ctaButton}>
            <span>{t('bottomCta.button')}</span>
            <ArrowRight size={18} />
          </Link>
        </section>
      </main>

      {/* Minimal Footer */}
      <footer className={styles.footer}>
        <Link href="/" className={styles.footerLink}>
          {t('backToHome')}
        </Link>
      </footer>
    </div>
  );
}
